/**
 * MKS-493 — a decorative emoji that exists ONLY in the Sparkle theme.
 *
 * Renders `null` in every other theme, and `null` whenever no `ThemeProvider`
 * is mounted (`useTheme()` falls back to `default`), which is why no existing
 * component test sees one.
 *
 * ## The rules, enforced here rather than at each call site
 *
 * - `aria-hidden="true"` always. The emoji carries no meaning a screen reader
 *   needs; without this, "Save" becomes "Save sparkles" in every reading, and
 *   the accessible name of a button would change — which would also break
 *   `getByRole('button', { name: 'Save' })` across the suite.
 *   Hidden nodes are excluded from accessible-name computation, so they are not.
 * - Always at the END, after a space. The leading space is part of THIS
 *   component, so a call site can neither forget it nor double it.
 * - At most one per element: that is a call-site rule, but the component being
 *   a single glyph makes breaking it obvious in review.
 *
 * ## Where it may NOT go
 *
 * Never on errors or warnings, never in numbers, times or data tables, and
 * never in an export. Exports are generated on the backend, where `data-theme`
 * does not reach, so that one is structural rather than a convention.
 */
import { useTheme } from '../../services/themeContext';

/** The three roles the design defines. Not a free-form emoji slot. */
export const THEME_EMOJI = {
  /** Brand — the logo and the page heading. */
  brand: '🌸',
  /** Main action — primary buttons, the welcome message, the Sparkle switch. */
  action: '✨',
  /** Joy / done — save and submit confirmations, the approved/done chip. */
  done: '💖',
} as const;

export type ThemeEmojiRole = keyof typeof THEME_EMOJI;

export interface ThemeEmojiProps {
  role: ThemeEmojiRole;
}

export function ThemeEmoji({ role }: ThemeEmojiProps) {
  const { theme } = useTheme();
  if (theme !== 'sparkle') return null;
  return (
    <span aria-hidden="true" className="ms-theme-emoji">
      {' '}
      {THEME_EMOJI[role]}
    </span>
  );
}
